import _ from 'lodash'
import { colors, date, Dialog, Notify, Platform, setCssVar } from 'quasar'
const { formatDate, addToDate } = date
import { isAdmin, isConsumer, isKds, isKiosk, isPos, isRoam, isSignage } from 'src/config'
import Model, { IsoDate, IsoDateTime } from 'src/models/Model'
import { isUrlSameHostName } from 'src/models/page/Navigation'

export default async ({ app }) => {
  const helper = {}

  helper.showErrors = (errors) => {
    Dialog.create({
      title: 'Message',
      message: _(errors).map(error => error?.networkError?.result?.error?.message || error?.description || error?.message || error).uniq().value().join(', ')
    })
  }

  helper.showOptions = (title, message, options, callback, noBackdropDismiss = false, defaultValue = null) => {
    Dialog.create({
      title,
      message,
      options: {
        type: 'radio',
        model: defaultValue || options[0].value,
        items: options
      },
      noBackdropDismiss,
      cancel: true
    }).onOk(callback)
  }

  helper.showMessage = (title, message, callback) => {
    Dialog.create({
      title,
      message,
      cancel: true
    }).onOk(callback)
  }

  helper.notify = (message, type = 'primary', position = 'bottom') => {
    Notify.create({
      message,
      type,
      position
    })
  }

  helper.notifyWithTitle = (title, message, type = 'primary', position = 'bottom') => {
    Notify.create({
      message: title,
      type,
      position,
      caption: message
    })
  }

  helper.success = (message) => {
    Notify.create({
      message,
      type: 'positive',
      position: 'top'
    })
  }

  helper.error = (message) => {
    Notify.create({
      position: 'top',
      type: 'negative',
      message
    })
  }

  helper.toLocalTime = (time) => {
    return new Date(new IsoDateTime(time).inTimeZone(Model.configuration?.currentSite?.timeZone))
  }

  helper.secondsToMs = (seconds) => {
    return seconds * 1000
  }

  helper.toLocalDate = (date) => {
    return new Date(new IsoDate(date).inTimeZone(Model.configuration?.currentSite?.timeZone))
  }

  helper.applyDrag = (arr, dragResult) => {
    const { removedIndex, addedIndex, payload } = dragResult
    if (removedIndex === null && addedIndex === null) return arr

    const result = [...arr]
    let itemToAdd = payload

    if (removedIndex !== null) {
      itemToAdd = result.splice(removedIndex, 1)[0]
    }

    if (addedIndex !== null) {
      result.splice(addedIndex, 0, itemToAdd)
    }

    return result
  }

  helper.asOptions = (array) => _.map(array, value => ({ label: value === null ? 'None' : _.startCase(value), value, slot: value }))

  const objectToArrayTestFn = (value) => value === true
  helper.objectToArray = (object, { testFn = objectToArrayTestFn } = {}) => {
    return _(object).pickBy(testFn).keys().value()
  }

  const arrayToObjectTestFn = (array, key) => array.includes(key)
  helper.arrayToObject = (array, { object = {}, allKeys = null, defaultValue = true, missingValue = false, testFn = arrayToObjectTestFn } = {}) => {
    return _(allKeys || array).keyBy().mapValues((_, key) => testFn(array, key) ? (object[key] || defaultValue) : missingValue).value()
  }

  helper.formatTime = (dateTime, showDate = false, relative = false) => {
    const today = formatDate(new Date(), 'YYYY-MM-DD')
    const tomorrow = formatDate(addToDate(new Date(), { days: 1 }), 'YYYY-MM-DD')

    const dateTimeDate = formatDate(dateTime, 'YYYY-MM-DD')

    const time = formatDate(dateTime, 'h:mm a')

    if (!showDate) {
      return time
    }

    let date = ''

    if (relative && dateTimeDate === today) {
      date = Model.app.i18n.t('time.today')
    } else if (relative && dateTimeDate === tomorrow) {
      date = Model.app.i18n.t('time.tomorrow')
    } else {
      date = formatDate(dateTime, 'MMM Do')
    }

    return `${date} at ${time}`
  }

  helper.foundNetlifyUrl = url => {
    let warningName = ''
    if (_.includes(url, '.netlify.app')) {
      warningName = 'netlify url'
    }
    return warningName
  }

  helper.setThemeColors = function (site) {
    setCssVar('primary', site.primaryColor)
    setCssVar('secondary', site.secondaryColor)
    setCssVar('accent', site.accentColor)
    setCssVar('dark', site.darkColor)
    setCssVar('positive', site.positiveColor)
    setCssVar('negative', site.negativeColor)
    setCssVar('info', site.infoColor)
    setCssVar('warning', site.warningColor)
    setCssVar('background', site.backgroundColor)
    setCssVar('splashBackground', site.splashBackgroundColor)
    setCssVar('seatSelected', site.seatSelectedColor)
    setCssVar('seatAvailable', site.seatAvailableColor)
    setCssVar('header', site.headerColor)
  }

  helper.resetThemeColors = function () {
    setCssVar('primary', '#027be3')
    setCssVar('secondary', '#26a69a')
    setCssVar('accent', '#9c27b0')
    setCssVar('dark', '#1d1d1d')
    setCssVar('positive', '#21ba45')
    setCssVar('negative', '#c10015')
    setCssVar('info', '#31ccec')
    setCssVar('warning', '#f2c037')
    setCssVar('background', '#eeeeee')
    setCssVar('splashBackground', '#eeeeee')
    setCssVar('seatSelected', '#027be3')
    setCssVar('seatAvailable', '#26a69a')
    setCssVar('header', '#027be3')
  }

  helper.setCustomPageStyles = (() => {
    const style = document.createElement('style')
    style.id = 'indy-custom-page-styles'
    document.head.append(style)
    return (styleString) => {
      style.textContent = styleString
    }
  })()

  helper.setSiteStyles = (() => {
    const style = document.createElement('style')
    style.id = 'indy-site-styles'
    document.head.append(style)
    return (styleString) => {
      style.textContent = styleString
    }
  })()

  function handleKeyboardHide () {
    window.scrollTo(0, 0)
    document.body.scrollTop = 0
  }

  // In Mobile Safari on iOS, there is a weird bug that happens after hidding the keyboard
  // The image is drawn correctly, but the touch even space is still associated with the body being
  // pushed off the screen. The makes it seem like the buttons don't work, etc.
  //
  // We can use focusout for times someone clicks on the pages somewhere else, but if the click the
  // button to hide the keyboard, we need to catch that event. The only way known is to set this
  // onblur handler for each item when they enter the dom
  const KEYBOARD_INPUT_TYPE = ['text', 'textarea', 'search', 'number', 'text', 'password', 'color', 'email', 'month', 'url', 'week', 'month', 'datetime-local']
  function findTextFieldsBindBlurListenders () {
    _.each(document.querySelectorAll('input, textarea'), el => {
      if (el.onblur) return

      if (!_.includes(KEYBOARD_INPUT_TYPE, el.type)) return

      el.onblur = handleKeyboardHide
    })

    setTimeout(findTextFieldsBindBlurListenders, 100)
  }

  if (Platform.is.ios) {
    findTextFieldsBindBlurListenders()
  }

  /**
   * Screen Functions
   */
  function isLandscapeView () {
    return app.config.globalProperties.$q.screen.height <= app.config.globalProperties.$q.screen.width
  }
  function getMaxWidthForScreenSize (isPhonePreview) {
    if (app.config.globalProperties.$q.screen.md || isPhonePreview) return '1230px'
    if (app.config.globalProperties.$q.screen.lg) return '1360px'
    if (app.config.globalProperties.$q.screen.xl) return '1600px'
    return '980px'
  }

  /**
   * Color Functions
   */
  function isColorDark (color) {
    return colors.luminosity(color) <= 0.4
  }
  function lightenColor (color, val) {
    return colors.lighten(color, val)
  }
  function getSiteColorHex (currentSite, color) {
    if (color === 'primary') return currentSite.primaryColor
    if (color === 'secondary') return currentSite.secondaryColor
    if (color === 'header') return currentSite.headerColor
    if (color === 'accent') return currentSite.accentColor
    if (color === 'positive') return currentSite.positiveColor
    if (color === 'negative') return currentSite.negativeColor
    if (color === 'info') return currentSite.infoColor
    if (color === 'warning') return currentSite.warningColor
    if (color === 'dark') return currentSite.darkColor
    if (color === 'seatSelected') return currentSite.seatSelectedColor
    return undefined
  }

  function getTextColor (currentSite, color) {
    return isColorDark(getSiteColorHex(currentSite, color)) ? 'text-white' : 'text-black'
  }

  function getColor (currentSite, color) {
    return isColorDark(getSiteColorHex(currentSite, color)) ? 'white' : 'black'
  }

  /**
   * Beep Functions
   */
  // if you have another AudioContext class use that one, as some browsers have a limit
  const audioCtx = new (window.AudioContext || window.webkitAudioContext || window.audioContext)()

  // duration of the tone in milliseconds. Default is 500
  // frequency of the tone in hertz. default is 440
  // volume of the tone. Default is 1, off is 0.
  // type of tone. Possible values are sine, square, sawtooth, triangle, and custom. Default is sine.
  // callback to use on end of tone
  function beep (duration, frequency, volume, type, callback) {
    const oscillator = audioCtx.createOscillator()
    const gainNode = audioCtx.createGain()

    oscillator.connect(gainNode)
    gainNode.connect(audioCtx.destination)

    if (volume) { gainNode.gain.value = volume }
    if (frequency) { oscillator.frequency.value = frequency }
    if (type) { oscillator.type = type }
    if (callback) { oscillator.onended = callback }

    oscillator.start(audioCtx.currentTime)
    oscillator.stop(audioCtx.currentTime + ((duration || 500) / 1000))
  }

  function piercingBeep () {
    beep(100, 1750, 1, 'triangle')
  }

  /**
   * Finds the closest ancestor component with the specified name.
   * Returns null if no component is found.
   */
  function findClosestParentByName (componentName, options = {}) {
    return findClosestParentMatching.call(
      this,
      component => component.$options.name === componentName,
      options
    )
  }

  /**
   * Node traversal functions
   */

  /**
   * Finds the closest ancestor Vue component for which the given
   * evaluator function returns a truthy value. Returns null if
   * no component is found.
   */
  function findClosestParentMatching (evaluatorFn, options = {}) {
    options = { maxDepth: 20, skipSelf: false, ...options }
    if (!options.skipSelf && evaluatorFn(this)) {
      return this
    }
    let counter = 0
    let parentComponent = this.$parent
    while (parentComponent && counter < options.maxDepth) {
      if (evaluatorFn(parentComponent)) {
        return parentComponent
      }
      parentComponent = parentComponent.$parent
      counter++
    }
    return null
  }

  /**
   * Permission functions
   */

  function isPermitted (...actions) {
    return Model.context.isPermitted(...actions)
  }

  function isAnyPermitted (...actions) {
    return Model.context.isAnyPermitted(...actions)
  }

  function isPreviewPermissionsPermitted () {
    return Model.context.isPreviewPermissionsPermitted
  }

  /**
   * Feature flag functions
   */

  function isFeatureEnabled (featureName) {
    return Model.context.isFeatureEnabled(featureName)
  }

  /**
   * SEO functions
   */

  const CANONICAL_LINK_TAG_ID = 'canonical-link-tag'

  /**
   * Adds a `<link rel="canonical" href="${currentUrl}">` tag to the document head
   * for SEO purposes. Should only be called when the current URL is considered canonical
   * for the current page.
   *
   * @param {string} urlPath The path of the URL to add the canonical link tag for.
   *    If passing in a full URL, the following flag should be set to `true`.
   * @param {boolean} isAbsoluteUrl Whether the URL path is an absolute URL.
   */
  function addCanonicalLinkTag (urlPath, isAbsoluteUrl = false) {
    if (!urlPath) return

    let absoluteUrl
    if (isAbsoluteUrl) {
      absoluteUrl = urlPath
    } else {
      if (!_.startsWith(urlPath, '/')) {
        urlPath = `/${urlPath}`
      }
      absoluteUrl = `${window.location.origin}${urlPath}`
    }

    let linkTag = document.getElementById(CANONICAL_LINK_TAG_ID)
    if (linkTag) {
      linkTag.setAttribute('href', absoluteUrl)
    } else {
      linkTag = document.createElement('link')
      linkTag.id = CANONICAL_LINK_TAG_ID
      linkTag.setAttribute('rel', 'canonical')
      linkTag.setAttribute('href', absoluteUrl)
      document.head.appendChild(linkTag)
    }

    return linkTag
  }

  /**
   * Removes the `<link rel="canonical" href="${currentUrl}">` tag from the document head.
   * Should be called when the current URL is no longer canonical for the current page.
   */
  function removeCanonicalLinkTag () {
    const linkTag = document.getElementById(CANONICAL_LINK_TAG_ID)
    if (linkTag) {
      linkTag.remove()
    }
  }

  // These methods alter the DOM and can be called many times in quick succession with
  // different URL paths as navigations/redirects are resolved. To avoid excessive layout
  // thrashing, we debounce these calls. These should share the same debounce threshold.
  // 34 ms allows for 2 frames at 60 Hz.
  const debouncedAddCanonicalLinkTag = _.debounce(addCanonicalLinkTag, 34)
  const debouncedRemoveCanonicalLinkTag = _.debounce(removeCanonicalLinkTag, 34)

  /**
   * Site swap functions
   */

  function changeCircuitOrSite () {
    return Model.context.changeCircuitOrSite()
  }

  /**
   * URL functions
   */

  /**
   * Return the URL string with any current URL query parameters appended to it. Any previous query
   * parameters are preserved but conflicting parameters are overwritten by the current query parameters.
   * This can be used to preserve/"forward" query parameters when navigating to a new page.
   * @param {string} url The URL string to append the current query parameters to.
   * @returns {string} The URL string with the current query parameters appended to it.
   * @example
   * // Returns 'https://example.com?foo=bar&baz=qux'
   * appendCurrentQueryParams('https://example.com?foo=bar')
   */
  function appendCurrentQueryParams (url) {
    if (!_.isString(url)) return url

    const [baseUrl, urlQueryString] = _.split(url, '?')
    const mergedQueryParams = new URLSearchParams(urlQueryString)

    // We use 'primary' to force switch sites. It shouldn't be propagated from current route.
    const currentQueryParams = _.omit(this.$route.query, 'primary')
    _.each(currentQueryParams, (value, key) => {
      mergedQueryParams.set(key, _.isEmpty(value) ? true : value)
    })
    return `${baseUrl}?${mergedQueryParams.toString()}`
  }

  app.config.globalProperties.$mh = helper
  Model.prototype.$mh = helper

  app.config.globalProperties.$admin = isAdmin
  app.config.globalProperties.$register = isPos
  app.config.globalProperties.$mobileRegister = () => app.config.globalProperties.$q.screen.lt.sm && app.config.globalProperties.$register
  app.config.globalProperties.$consumer = isConsumer
  app.config.globalProperties.$signage = isSignage
  app.config.globalProperties.$kiosk = isKiosk
  app.config.globalProperties.$kds = isKds
  app.config.globalProperties.$roam = isRoam
  app.config.globalProperties.$station = isKiosk || isPos

  app.config.globalProperties.$handleKeyboardHide = handleKeyboardHide

  app.config.globalProperties.$isLandscapeView = isLandscapeView
  app.config.globalProperties.$getMaxWidthForScreenSize = getMaxWidthForScreenSize

  app.config.globalProperties.$isColorDark = isColorDark
  app.config.globalProperties.$lightenColor = lightenColor
  app.config.globalProperties.$getSiteColorHex = getSiteColorHex
  app.config.globalProperties.$getTextColor = getTextColor
  app.config.globalProperties.$getColor = getColor

  app.config.globalProperties.$beep = beep
  app.config.globalProperties.$piercingBeep = piercingBeep

  app.config.globalProperties.$findClosestParentByName = findClosestParentByName
  app.config.globalProperties.$findClosestParentMatching = findClosestParentMatching

  app.config.globalProperties.$isPermitted = isPermitted
  app.config.globalProperties.$isAnyPermitted = isAnyPermitted
  app.config.globalProperties.$isPreviewPermissionsPermitted = isPreviewPermissionsPermitted

  app.config.globalProperties.$isFeatureEnabled = isFeatureEnabled

  // See above comment for reasoning on debouncing these methods.
  app.config.globalProperties.$addCanonicalLinkTag = debouncedAddCanonicalLinkTag
  app.config.globalProperties.$removeCanonicalLinkTag = debouncedRemoveCanonicalLinkTag

  app.config.globalProperties.$changeCircuitOrSite = changeCircuitOrSite

  app.config.globalProperties.$appendCurrentQueryParams = appendCurrentQueryParams
  app.config.globalProperties.$isUrlSameHostName = isUrlSameHostName

  const search = {}
  search.show = function (value = '') {
    search.handleShow &&
      search.handleShow(value)
  }
  app.config.globalProperties.$search = search
}
